import type { SettingsTemplateModel } from '@/shared/generated'

import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Slot } from 'radix-ui'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useWithAffectedOGsHandler } from '@/entities/optimization-group'
import { useApplySettingsTemplateMutation } from '@/entities/settings-template'

import { confirm } from '@/shared/components/ui/confirmer'
import { ApplyAction, SettingsTemplateType } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'

export const ApplyPricingCampaignTemplate = ({
	pricingCampaignId,
	children,
	action = ApplyAction.UpdatePricingCampaignSettings,
	onMutate,
}: {
	children: React.ReactNode
	action?: ApplyAction
	pricingCampaignId: string
	onMutate?: () => void
}) => {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const queryClient = useQueryClient()
	const applyTemplateMutationBase = useApplySettingsTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['optimizations'] })
			queryClient.invalidateQueries({ queryKey: ['pricing-campaigns'] })
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({ queryKey: ['pricing-campaign'] })
			onMutate?.()
		},
	})
	const applyTemplateMutation = useWithAffectedOGsHandler(
		applyTemplateMutationBase
	)
	const handleApply = async ({ id }: SettingsTemplateModel) => {
		ampli.settingsDropdownApplyTemplate()
		const answer = await confirm({
			title: t('template.apply.confirm.title', {
				defaultValue: 'Apply template',
			}),
			description: t('pc.apply_template.confirm.text', {
				defaultValue:
					'Are you sure you want to apply the template and overwrite the Pricing campaign(s) settings?',
			}),
			actionText: t('general_apply'),
			cancelText: t('general_cancel'),
		})
		if (!answer) return

		toast.promise(
			applyTemplateMutation.mutateAsync({
				id,
				data: {
					ids: [pricingCampaignId],
					action,
				},
			}),
			{
				loading: t('general_processing'),
				success: t('toast.entity.updated', {
					entity: t('entity.pricing_campaign'),
				}),
				error: t('toast.entity.failed', {
					entity: t('entity.pricing_campaign'),
				}),
			}
		)
	}
	const handleClick = () => {
		showModal('SETTING_TEMPLATES_MODAL', {
			template_types: [SettingsTemplateType.PricingCampaign],
			handleApply,
		})
	}

	return <Slot.Root onClick={handleClick}>{children}</Slot.Root>
}
